/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    createStore, applyMiddleware, compose, combineReducers,
} from 'redux';
import { createBrowserHistory, createMemoryHistory } from 'history';
import createSagaMiddleware, { END } from 'redux-saga';
import { all } from 'redux-saga/effects';
import { persistReducer } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session'; // defaults to localStorage for web and AsyncStorage for react-native

import MBPLogger from 'mbp-logger';

import mbpUtil from 'mbp-api-util';
import checkoutWatchers from './ducks/MfeCheckout/MfeCheckout-Operations';
import AppReducer from './ducks/App';
import PaymentReducer from './ducks/MfeCheckout/ducks/Payment';
import CartReducer from './ducks/MfeCheckout/ducks/Cart';
import DonationReducer from './ducks/MfeCheckout/ducks/Donation';
import AttributionReducer from './ducks/MfeCheckout/ducks/Attribution';
import orderPersistReducer from './ducks/Order';
import commonPersistReducer from './ducks/Common';
import memberReducers from './ducks/MfeCheckout/ducks/Member';
import UIReducer from './ducks/Member/ducks/UI-Reducer/UI-Reducer';
import reducers from './ducks/Checkout';
import passportPersistReducer from './ducks/MfeCheckout/ducks/Passport';
import PageReducers from './ducks/Member/ducks/Page-Reducers/Page-Reducers';
import cartReducer from './ducks/AddToCart/AddToCart-Reducer';
import recipientPersistReducer from './ducks/Recipient';
import { duck as tmDuck } from './ducks/TagManager';
import clickStreamWatchers from './ducks/TagManager/ducks/ClickStreamEvents';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

// MFE checkout sagas
function* rootSaga() {
    if (REACT_APP_INJECTABLE === 'false') {
        yield all([
            ...checkoutWatchers,
            ...clickStreamWatchers,
            ...tmDuck.operations.watchers,
        ]);
    } else {
        yield all([
            ...checkoutWatchers,
        ]);
    }
}

// MFE checkout reducers
let rootReducer = {
    app: AppReducer,
    payment: PaymentReducer,
    cart: CartReducer,
    donation: DonationReducer,
    attribution: AttributionReducer,
};

if (REACT_APP_INJECTABLE === 'false') {
    rootReducer = {
        app: AppReducer,
        payment: PaymentReducer,
        cart: CartReducer,
        donation: DonationReducer,
        attribution: AttributionReducer,
        order: orderPersistReducer,
        common: commonPersistReducer,
        member: memberReducers,
        ui: UIReducer,
        checkout: reducers,
        passport: passportPersistReducer,
        page: PageReducers,
        addToCart: cartReducer,
        recipient: recipientPersistReducer,
        tagManager: tmDuck.default,
    };
}

export const configureHistory = (url) => {
    let history = null;

    if (typeof window !== 'undefined') {
        history = createBrowserHistory();
    } else {
        const config = {};
        if (url) {
            config.initialEntries = [url];
        }
        history = createMemoryHistory(config);
    }

    return history;
};

/* eslint-disable no-underscore-dangle */
const sagaMiddleware = createSagaMiddleware();
const devTools =  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true })
    : () => (noop) => noop;
const persistConfig = {
    key: 'root',
    storage: sessionStorage,
    whitelist: ['member'],
};

// runSaga is middleware.run function
// rootSaga is a your root saga for static sagas
function createSagaInjector(runSaga, currentRootSaga) {
    // Create a dictionary to keep track of injected sagas
    const injectedSagas = new Map();

    const isInjected = (key) => injectedSagas.has(key);

    const injectSaga = (key, saga) => {
        // We won't run saga if it is already injected
        if (isInjected(key)) return;

        // Sagas return task when they executed, which can be used
        // to cancel them
        const task = runSaga(saga);

        // Save the task if we want to cancel it in the future
        injectedSagas.set(key, task);
    };

    // Inject the root saga as it a staticlly loaded file,
    injectSaga('root', currentRootSaga);

    return injectSaga;
}

function createReducer(asyncReducers) {
    const allReducer = combineReducers({
        ...rootReducer,
        ...asyncReducers,
    });
    const persistedReducer = persistReducer(persistConfig, allReducer);
    return persistedReducer;
}

const configureStore = (initialState = {}) => {
    // const persistedReducer = persistReducer(persistConfig, rootReducer);
    const middlewares = [
        sagaMiddleware, // Redux-Saga
        MBPLogger.middlewareLogger.logger, // MBP-Logger
        MBPLogger.middlewareLogger.crashReporter, // MBP-Logger
    ];
    const enhancers = [applyMiddleware(...middlewares), devTools()];
    const composedEnhancers = compose(...enhancers);
    const store = createStore(createReducer(), initialState, composedEnhancers);
    store.runSaga = sagaMiddleware.run;

    // Add injectSaga method to our store
    store.injectSaga = createSagaInjector(sagaMiddleware.run, rootSaga);

    // Add a dictionary to keep track of the registered async reducers
    store.asyncReducers = {};

    // Create an inject reducer function
    // This function adds the async reducer, and creates a new combined reducer
    store.injectReducer = (key, asyncReducer) => {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    };

    store.close = () => {
        store.dispatch(END);
    };

    return store;
};

export default configureStore;
